import { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  Button,
  FieldError,
  Form,
  Label,
  ListBox,
  Modal,
  Select,
  Switch,
} from "@heroui/react";
import { Icon } from "@iconify/react";

import { useCreateDirection } from "@/features/directions/use-directions.ts";
import { useCities } from "@/features/city/use-cities.ts";

interface IFormInput {
  fromCityId: string;
  toCityId: string;
  isActive: boolean;
}

export function CreateDirectionModal() {
  const [isOpen, setIsOpen] = useState(false);

  const { data } = useCities();
  const cities = data.data.map((item) => ({
    key: `${item.id}`,
    title: item.name,
  }));

  const { mutateAsync, isPending } = useCreateDirection({
    onSuccess: () => {
      reset();
      setIsOpen(false);
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<IFormInput>();

  const onSubmit: SubmitHandler<IFormInput> = (data) =>
    mutateAsync({
      fromCityId: Number(data.fromCityId),
      toCityId: Number(data.toCityId),
      isActive: data.isActive,
    });

  return (
    <div>
      <Button
        variant="primary"
        onPress={() => setIsOpen(true)}
      >
        Создать
        <Icon className="size-6" icon="solar:add-circle-bold" />
      </Button>

      <Modal isOpen={isOpen} onOpenChange={setIsOpen}>
        <Modal.Backdrop isDismissable variant="blur">
          <Modal.Container placement="center" size="lg">
            <Modal.Dialog>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Modal.Header>
                <Modal.Heading>Создать новое направление</Modal.Heading>
              </Modal.Header>
              <Modal.Body className="grid grid-cols-2 gap-4">
                <Controller
                  control={control}
                  name="fromCityId"
                  rules={{ required: "Нужно заполнить поле" }}
                  render={({ field }) => (
                    <Select
                      selectedKey={field.value ?? null}
                      onSelectionChange={(key) => field.onChange(key)}
                      isInvalid={!!errors.fromCityId?.message}
                    >
                      <Label>Откуда</Label>
                      <Select.Trigger>
                        <Select.Value />
                        <Select.Indicator />
                      </Select.Trigger>
                      <FieldError>{errors.fromCityId?.message}</FieldError>
                      <Select.Popover>
                        <ListBox items={cities}>
                          {({ key, title }) => (
                            <ListBox.Item id={key} textValue={title}>
                              {title}
                            </ListBox.Item>
                          )}
                        </ListBox>
                      </Select.Popover>
                    </Select>
                  )}
                />

                <Controller
                  control={control}
                  name="toCityId"
                  rules={{ required: "Нужно заполнить поле" }}
                  render={({ field }) => (
                    <Select
                      selectedKey={field.value ?? null}
                      onSelectionChange={(key) => field.onChange(key)}
                      isInvalid={!!errors.toCityId?.message}
                    >
                      <Label>Куда</Label>
                      <Select.Trigger>
                        <Select.Value />
                        <Select.Indicator />
                      </Select.Trigger>
                      <FieldError>{errors.toCityId?.message}</FieldError>
                      <Select.Popover>
                        <ListBox items={cities}>
                          {({ key, title }) => (
                            <ListBox.Item id={key} textValue={title}>
                              {title}
                            </ListBox.Item>
                          )}
                        </ListBox>
                      </Select.Popover>
                    </Select>
                  )}
                />

                <Controller
                  control={control}
                  name="isActive"
                  defaultValue={true}
                  render={({ field }) => (
                    <Switch isSelected={field.value} onChange={field.onChange}>
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                      <Switch.Content>
                        <Label>Видимое</Label>
                      </Switch.Content>
                    </Switch>
                  )}
                />
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="danger-soft"
                  onPress={() => setIsOpen(false)}
                >
                  Отмена
                </Button>

                <Button variant="primary" isPending={isPending} type="submit">
                  Создать
                </Button>
              </Modal.Footer>
            </Form>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>
    </div>
  );
}
